import React, { createContext, useContext, useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('dark');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load theme from localStorage first for immediate effect
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    applyTheme(savedTheme);
    setLoading(false);
  }, []);

  const applyTheme = (newTheme) => {
    const root = document.documentElement;
    if (newTheme === 'light') {
      root.classList.add('light-mode');
      root.classList.remove('dark-mode');
    } else {
      root.classList.add('dark-mode');
      root.classList.remove('light-mode');
    }
  };

  const toggleTheme = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('theme', newTheme);

    // Save to backend
    try {
      await fetch(`${API}/settings/theme`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ theme: newTheme }),
      });
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  const fetchThemeFromServer = async () => {
    try {
      const response = await fetch(`${API}/settings`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.theme && data.theme !== theme) {
          setTheme(data.theme);
          applyTheme(data.theme);
          localStorage.setItem('theme', data.theme);
        }
      }
    } catch (error) {
      console.error('Failed to fetch theme:', error);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, loading, fetchThemeFromServer }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
